import {FC, useState} from 'react';
import {usePersona} from 'src/permissions/hooks';
import {Form, Formik, FormikProps} from 'formik';
import {Box, Grid, Tab, Tabs, TabsProps} from '@mui/material';
import {useIntl} from 'react-intl';
import {AMPInput} from '../../common/AMPInput';
import {getHelperText} from '../../../utils/forms';
import {CancelButton} from '../../common/CancelButton';
import {SubmitButton} from '../../common/SubmitButton';
import {tagsApi} from '../../../api';
import {preloaderChangeStatus} from '../../../stores/common/commonSlice';
import {useAppDispatch, useAppSelector, useBusinessId} from '../../../app/hooks';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {CreateRfidFormFromDedicatedPageProps} from './CreateRfidFormFromDedicatedPage.types';
import {actionsWrapperSx, formContentSx, formSx, tabSx,} from './CreateRfidFormFromDedicatedPage.sx';
import {TabPanel} from 'src/components/common/TabPanel';
import * as Yup from 'yup';
import {PrivateDriverForRfidDropdown} from './PrivateDriverForRfidDropdown';
import {CompanyForRfidDropdown} from './CompanyForRfidDropdown';
import {EmployeeForRfidDropdown} from './EmployeeForRfidDropdown';
import {ManagerForRfidDropdown} from './ManagerForRfidDropdown';
import {CreateRfidRequest} from 'src/types/tags';
import {UpdateRfidRequestForDedicatedPage, UserDTO} from 'src/types';

const getInitialValue = (isCompany: boolean, companyId?: number): CreateRfidFormFromType => ({
    idTag: '',
    licensePlate: '',
    companyId: isCompany ? companyId || null : null,
    drivers: [{id: undefined}], // Initialize with one empty driver
    employeeId: null,
});

export const basicCeateRfidFormFromDedicatedPageFormSchema = {
    idTag: Yup.string().max(255).required('schemaRequiredRfid'),
    licensePlate: Yup.string().max(32).nullable(),
};

export const businessCreateRfidFormFromDedicatedPageFormSchema = Yup.object().shape({
    ...basicCeateRfidFormFromDedicatedPageFormSchema,
    companyId: Yup.number().nullable(),
    driverIds: Yup.array().of(Yup.number()).nullable(),
    employeeId: Yup.number().nullable(),
});

export interface CreateRfidFormFromType {
    idTag: string;
    licensePlate?: string;
    companyId?: number | null;
    drivers?: Array<Partial<UserDTO>>;
    employeeId?: number | null;
    businessId?: number | null;
    managerId?: number | null;
}

export const CreateRfidFormFromDedicatedPage: FC<CreateRfidFormFromDedicatedPageProps> = ({
                                                                                              handleClose,
                                                                                              initialValues,
                                                                                              additionalSubmitAction,
                                                                                          }) => {
    const isEditMode = Boolean(initialValues && Object.keys(initialValues).length);
    const defaultTabNumber = isEditMode ? (initialValues?.drivers?.length ? 0 : 1) : 0;


    const persona = usePersona();
    const intl = useIntl();
    const dispatch = useAppDispatch();

    const {authInfo} = useAppSelector((state) => state.common);
    const businessId = useBusinessId();
    const companyId = authInfo?.company?.id;
    const isSuperAdmin = persona.isPlatformAdmin;
    const isBusiness = persona.isBusinessOwner;
    // The business flow is "issue a tag on behalf of the business", which is what back-office staff
    // do regardless of which manager title they hold.
    const isManager = persona.isBackOffice && !persona.isBusinessOwner;
    const isTechnicalManager = false;
    const isCompany = persona.isCompany;
    // The platform administrator issues tags the same way a business does - to a driver, to a
    // company's employee, to a manager - now that they work inside one business. They used to get a
    // stripped-down form of their own with a business picker and no holder at all, which produced a
    // tag belonging to nobody.
    const isBusinessFlow = isBusiness || isManager || isTechnicalManager || isSuperAdmin;
    const isCompanyFlow = isCompany;

    const [createNewTagTrigger] = tagsApi.useCreateNewTagMutation();
    const [updateTagTrigger] = tagsApi.useUpdateTagMutation();

    const [tab, setTab] = useState<number>(defaultTabNumber);

    const handleSetTab: TabsProps['onChange'] = (_event, newValue) => {
        setTab(newValue);
    };


    const handleSubmitAction = async (values: CreateRfidFormFromType) => {
        try {
            dispatch(preloaderChangeStatus(true));

            // ! Edit case
            if (isEditMode) {
                const tagId = initialValues?.tagId ?? 0;

                const input: UpdateRfidRequestForDedicatedPage = {
                    tagId,
                    data: {
                        blocked: initialValues?.blocked ?? false,
                        businessId,
                        idTag: values.idTag,
                        licensePlate: values.licensePlate?.trim() ? values.licensePlate.trim() : null,
                    },
                };

                if (tab === 1 && values?.companyId && isBusinessFlow) {
                    input.data.companyId = values.companyId;
                }

                if (tab === 1 && values?.employeeId && isBusinessFlow) {
                    input.data.employeeId = values.employeeId;
                }

                // Add driver user ID directly to update request (same as company approach)
                if (tab === 0 && values?.drivers && values.drivers?.length > 0 && isBusinessFlow) {
                    const driverUserId = values.drivers.find(driver => driver.id)?.id;
                    if (driverUserId) {
                        input.data.userId = driverUserId;
                    }
                }

                // Add manager ID to update request
                if (tab === 2 && values?.managerId && isBusinessFlow) {
                    input.data.managerId = values.managerId;
                }

                // Company flow - always use current company ID and employee ID
                if (isCompanyFlow && companyId) {
                    input.data.companyId = companyId;
                    if (values?.employeeId) {
                        input.data.employeeId = values.employeeId;
                    }
                }

                await updateTagTrigger(input).unwrap();

                return;
            }

            // ! Create a case
            const input: CreateRfidRequest = {
                data: {
                    businessId,
                    idTag: values.idTag,
                    licensePlate: values.licensePlate?.trim() ? values.licensePlate.trim() : null,
                },
            };

            if (tab === 1 && values?.companyId && isBusinessFlow) {
                input.data.companyId = values.companyId;
            }

            if (tab === 1 && values?.employeeId && isBusinessFlow) {
                input.data.employeeId = values.employeeId;
            }

            // Add driver ID directly to creation request (same as company approach)
            if (tab === 0 && values?.drivers && values.drivers?.length > 0 && isBusinessFlow) {
                const driverId = values.drivers.find(driver => driver.id)?.id;
                if (driverId) {
                    input.data.driverId = driverId;
                }
            }

            // Add manager ID to creation request
            if (tab === 2 && values?.managerId && isBusinessFlow) {
                input.data.managerId = values.managerId;
            }

            // Company flow - always use current company ID and employee ID
            if (isCompanyFlow && companyId) {
                input.data.companyId = companyId;
                if (values?.employeeId) {
                    input.data.employeeId = values.employeeId;
                }
            }

            await createNewTagTrigger(input).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            if (additionalSubmitAction) {
                additionalSubmitAction(values.idTag);
            }

            handleClose();
            dispatch(preloaderChangeStatus(false));
        }
    };

    return (
        <Formik
            initialValues={isEditMode ? ({...initialValues} as CreateRfidFormFromType) : getInitialValue(isCompany, companyId)}
            validationSchema={businessCreateRfidFormFromDedicatedPageFormSchema}
            onSubmit={handleSubmitAction}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<CreateRfidFormFromType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    {/* Business user form */}
                    {isBusinessFlow && (
                        <>
                            <Box sx={{borderBottom: 1, borderColor: 'divider', px: 3}}>
                                <Tabs
                                    value={tab}
                                    onChange={handleSetTab}
                                >
                                    <Tab
                                        sx={tabSx}
                                        label={intl.formatMessage({id: 'privateDriverTabForRfidDialog'})}
                                    />
                                    <Tab
                                        sx={tabSx}
                                        label={intl.formatMessage({id: 'companyTabForRfidDialog'})}
                                    />
                                    <Tab
                                        sx={tabSx}
                                        label={intl.formatMessage({id: 'managerTabForRfidDialog'})}
                                    />
                                </Tabs>
                            </Box>

                            <Box sx={formSx}>
                                <Box sx={formContentSx}>
                                    {/* General form field for both tabs */}
                                    {/* RFID tag */}
                                    <Grid container>
                                        <Grid
                                            xs={12}
                                            item
                                        >
                                            <AMPInput
                                                value={values['idTag']}
                                                type={'idTag'}
                                                label={'idTag'}
                                                input={'idTag'}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                hasError={touched['idTag'] && Boolean(errors['idTag'])}
                                                helperText={getHelperText('idTag', touched, errors, intl)}
                                            />
                                        </Grid>
                                        <Grid
                                            xs={12}
                                            item
                                        >
                                            <AMPInput
                                                value={values['licensePlate'] ?? ''}
                                                type={'text'}
                                                label={'licensePlate'}
                                                input={'licensePlate'}
                                                placeholder={'licensePlatePlaceholder'}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                hasError={touched['licensePlate'] && Boolean(errors['licensePlate'])}
                                                helperText={getHelperText('licensePlate', touched, errors, intl)}
                                            />
                                        </Grid>
                                    </Grid>

                                    {/* Private driver tab */}
                                    <TabPanel
                                        value={tab}
                                        index={0}
                                    >
                                        {/* Private driver - only one driver allowed */}
                                        <Grid container>
                                            <Grid
                                                xs={12}
                                                item
                                            >
                                                <PrivateDriverForRfidDropdown
                                                    id={values?.drivers?.[0]?.id ?? null}
                                                    index={0}
                                                />
                                            </Grid>
                                        </Grid>
                                    </TabPanel>

                                    {/* Company tab */}
                                    <TabPanel
                                        value={tab}
                                        index={1}
                                    >
                                        <Grid container>
                                            <Grid
                                                xs={12}
                                                item
                                            >
                                                {/* Company */}
                                                <CompanyForRfidDropdown/>

                                                {/* Employee */}
                                                <EmployeeForRfidDropdown/>
                                            </Grid>
                                        </Grid>
                                    </TabPanel>

                                    {/* Manager tab */}
                                    <TabPanel
                                        value={tab}
                                        index={2}
                                    >
                                        <Grid container>
                                            <Grid
                                                xs={12}
                                                item
                                            >
                                                <ManagerForRfidDropdown/>
                                            </Grid>
                                        </Grid>
                                    </TabPanel>
                                </Box>
                            </Box>
                        </>
                    )}

                    {/* Company user form */}
                    {isCompanyFlow && (
                        <Box sx={formSx}>
                            <Box sx={formContentSx}>
                                {/* RFID tag */}
                                <Grid container>
                                    <Grid
                                        xs={12}
                                        item
                                    >
                                        <AMPInput
                                            value={values['idTag']}
                                            type={'idTag'}
                                            label={'idTag'}
                                            input={'idTag'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['idTag'] && Boolean(errors['idTag'])}
                                            helperText={getHelperText('idTag', touched, errors, intl)}
                                        />
                                    </Grid>
                                    <Grid
                                        xs={12}
                                        item
                                    >
                                        <AMPInput
                                            value={values['licensePlate'] ?? ''}
                                            type={'text'}
                                            label={'licensePlate'}
                                            input={'licensePlate'}
                                            placeholder={'licensePlatePlaceholder'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['licensePlate'] && Boolean(errors['licensePlate'])}
                                            helperText={getHelperText('licensePlate', touched, errors, intl)}
                                        />
                                    </Grid>
                                </Grid>

                                {/* Employee - only show employee dropdown for company */}
                                <Grid container>
                                    <Grid
                                        xs={12}
                                        item
                                    >
                                        <EmployeeForRfidDropdown/>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    )}

                    <Box sx={actionsWrapperSx}>
                        <CancelButton
                            text="cancel"
                            handler={handleClose}
                        />
                        <SubmitButton
                            text="save"
                            color="primary"
                            isSubmitting={isSubmitting}
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
